import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useT } from '../i18n';
import './RegistrationWizard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const RegistrationWizard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [tokenError, setTokenError] = useState('');
  const registrationToken = searchParams.get('token');

  // Step 1 - Krijo llogarinë (Login/Register)
  const [step1, setStep1] = useState({
    username: '',
    email: '',
    fjalekalimi: '',
    konfirmoFjalekalimin: ''
  });

  // Step 2 - Kompania
  const [step2, setStep2] = useState({
    emriKompanise: '',
    adresa: '',
    zipCode: '',
    qyteti: '',
    shteti: '',
    llojiKompanise: '',
    sektoriKompanise: '',
    aktivitetetOnSite: '',
    aktivitetetOffSite: '',
    targetiQendrueshmrise: '',
    qellimiPerdorimit: '',
    perQfareLlojRaportimi: ''
  });

  // Step 3 - Ndërtesat
  const [step3, setStep3] = useState({
    emri: '',
    ndertesa: '',
    adresa: '',
    qyteti: '',
    shteti: '',
    vitiNdertimit: '',
    madhesia: '',
    numriKateve: '',
    furnizimetUje: '',
    burimiKryesorEnergji: '',
    burimiDytesorEnergji: ''
  });

  // Validate registration token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!registrationToken) {
        setTokenValid(false);
        setTokenError(t('regw.tokenMissing'));
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/registration-tokens/validate?token=${registrationToken}`);
        
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          let errorMsg = t('regw.tokenInvalid');
          if (response.data.error === 'expired') {
            errorMsg = t('regw.tokenExpired');
          } else if (response.data.error === 'used') {
            errorMsg = t('regw.tokenUsed');
          }
          setTokenError(errorMsg);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenValid(false);
        setTokenError(t('regw.tokenVerifyFailed'));
      }
    };

    validateToken();
  }, [registrationToken]);

  // Load saved draft from localStorage
  useEffect(() => {
    const savedDraft = localStorage.getItem('registrationDraft');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        /*
         * Never restore a password from a draft. It used to come back
         * pre-filled as dots, so someone resuming an abandoned registration
         * submitted a password they had typed days earlier without realising
         * it, then could not log in with the one they thought they had just
         * chosen. Always make the password a deliberate act.
         */
        if (parsed.step1) setStep1({ ...parsed.step1, fjalekalimi: '', konfirmoFjalekalimin: '' });
        if (parsed.step2) setStep2(parsed.step2);
        if (parsed.step3) setStep3(parsed.step3);
        if (parsed.currentStep) setCurrentStep(parsed.currentStep);
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
  }, []);

  // Save draft on change
  useEffect(() => {
    // The draft is a convenience for long forms, not a credential store: keep
    // the password out of it entirely rather than writing it to disk on every
    // keystroke and hoping the success path clears it.
    const { fjalekalimi, konfirmoFjalekalimin, ...step1WithoutSecrets } = step1;
    localStorage.setItem('registrationDraft', JSON.stringify({
      step1: step1WithoutSecrets,
      step2,
      step3,
      currentStep
    }));
  }, [step1, step2, step3, currentStep]);

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};
    if (!step1.username.trim()) newErrors.username = t('regw.requiredField');
    if (!step1.email.trim()) newErrors.email = t('regw.requiredField');
    else if (!/\S+@\S+\.\S+/.test(step1.email)) newErrors.email = t('regw.invalidEmail');
    if (!step1.fjalekalimi) newErrors.fjalekalimi = t('regw.requiredField');
    else if (step1.fjalekalimi.length < 6) newErrors.fjalekalimi = t('regw.passwordMinLength');
    if (!step1.konfirmoFjalekalimin) newErrors.konfirmoFjalekalimin = t('regw.requiredField');
    else if (step1.fjalekalimi !== step1.konfirmoFjalekalimin) newErrors.konfirmoFjalekalimin = t('regw.passwordsMismatch');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!step2.emriKompanise.trim()) newErrors.emriKompanise = t('regw.requiredField');
    if (!step2.adresa.trim()) newErrors.adresa = t('regw.requiredField');
    if (!step2.zipCode.trim()) newErrors.zipCode = t('regw.requiredField');
    if (!step2.qyteti) newErrors.qyteti = t('regw.requiredField');
    if (!step2.shteti) newErrors.shteti = t('regw.requiredField');
    if (!step2.llojiKompanise) newErrors.llojiKompanise = t('regw.requiredField');
    if (!step2.sektoriKompanise) newErrors.sektoriKompanise = t('regw.requiredField');
    if (!step2.aktivitetetOnSite) newErrors.aktivitetetOnSite = t('regw.requiredField');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!step3.emri.trim()) newErrors.emri = t('regw.requiredField');
    if (!step3.ndertesa) newErrors.ndertesa = t('regw.requiredField');
    if (!step3.adresa.trim()) newErrors.adresa = t('regw.requiredField');
    if (!step3.qyteti.trim()) newErrors.qyteti = t('regw.requiredField');
    if (!step3.shteti) newErrors.shteti = t('regw.requiredField');
    if (!step3.vitiNdertimit) newErrors.vitiNdertimit = t('regw.requiredField');
    else if (isNaN(Number(step3.vitiNdertimit)) || Number(step3.vitiNdertimit) < 1800 || Number(step3.vitiNdertimit) > new Date().getFullYear()) {
      newErrors.vitiNdertimit = t('regw.invalidYear');
    }
    if (!step3.madhesia) newErrors.madhesia = t('regw.requiredField');
    else if (isNaN(Number(step3.madhesia)) || Number(step3.madhesia) <= 0) {
      newErrors.madhesia = t('regw.mustBePositive');
    }
    if (step3.numriKateve && (isNaN(Number(step3.numriKateve)) || Number(step3.numriKateve) < 1)) {
      newErrors.numriKateve = t('regw.mustBeWholeNumberMin');
    }
    if (!step3.burimiKryesorEnergji) newErrors.burimiKryesorEnergji = t('regw.requiredField');
    if (!step3.furnizimetUje) newErrors.furnizimetUje = t('regw.requiredField');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation handlers
  const handleNext = () => {
    let valid = false;
    if (currentStep === 1) valid = validateStep1();
    else if (currentStep === 2) valid = validateStep2();

    if (valid) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setSubmitError('');
    setSubmitSuccess('');

    const payload = {
      // User credentials from step 1
      user: {
        username: step1.username,
        email: step1.email,
        password: step1.fjalekalimi
      },
      // Company info from step 2
      kompania: {
        emri: step2.emriKompanise,
        adresa: step2.adresa,
        zipCode: step2.zipCode.trim(),
        qyteti: step2.qyteti,
        shteti: step2.shteti,
        llojiKompanise: step2.llojiKompanise,
        sektoriKompanise: step2.sektoriKompanise
      },
      // Activities from step 2
      aktivitetet: {
        aktivitetetOnSite: step2.aktivitetetOnSite,
        aktivitetetOffSite: step2.aktivitetetOffSite,
        targetiQendrueshmrise: step2.targetiQendrueshmrise,
        qellimiPerdorimit: step2.qellimiPerdorimit,
        perQfareLlojRaportimi: step2.perQfareLlojRaportimi
      },
      // Building info from step 3
      ndertesa: {
        emri: step3.emri,
        ndertesa: step3.ndertesa,
        adresa: step3.adresa,
        qyteti: step3.qyteti,
        shteti: step3.shteti,
        vitiNdertimit: step3.vitiNdertimit ? Number(step3.vitiNdertimit) : null,
        madhesia: Number(step3.madhesia),
        numriKateve: step3.numriKateve ? Number(step3.numriKateve) : null,
        furnizimetUje: step3.furnizimetUje,
        burimiKryesorEnergji: step3.burimiKryesorEnergji,
        burimiDytesorEnergji: step3.burimiDytesorEnergji || null
      },
      // Include registration token
      token: registrationToken
    };

    console.log('🚀 Submitting registration...');
    console.log('📤 Payload:', JSON.stringify(payload, null, 2));
    console.log('🌐 API URL:', `${API_URL}/api/registration-complete`);

    try {
      console.log('⏳ Sending request...');
      const response = await axios.post(`${API_URL}/api/registration-complete`, payload);
      console.log('✅ Registration successful!');
      console.log('📥 Response:', response.data);
      
      // Store the JWT token for auto-login
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        console.log('🔑 Token stored in localStorage');
      }
      
      setSubmitSuccess(t('regw.registrationSuccess'));
      localStorage.removeItem('registrationDraft');
      
      // Redirect to home/dashboard after 2 seconds
      setTimeout(() => {
        console.log('🔄 Redirecting to home...');
        navigate('/');
        window.location.reload(); // Reload to update auth state
      }, 2000);
    } catch (err) {
      console.error('❌ Registration error:', err);
      console.error('📛 Error response:', err.response?.data);
      console.error('📛 Error status:', err.response?.status);
      console.error('📛 Full error:', err);
      setSubmitError(err.response?.data?.message || t('regw.registrationFailed'));
    }
  };

  // Show loading state while validating token
  if (tokenValid === null) {
    return (
      <div className="wizard-container">
        <div className="wizard-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>{t('regw.verifyingLink')}</h2>
            <p>{t('regw.pleaseWait')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <div className="wizard-container">
        <div className="wizard-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>{t('regw.invalidLinkTitle')}</h2>
            <p style={{ marginBottom: '20px', fontSize: '16px' }}>{tokenError}</p>
            <p style={{ color: '#7f8c8d' }}>
              {t('regw.contactSupportHelp')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <div className="wizard-card">
        {/* Progress Header */}
        <div className="wizard-progress">
          <div className="progress-step-wrapper">
            <div className={`progress-circle ${currentStep >= 1 ? 'completed' : ''} ${currentStep === 1 ? 'current' : ''}`}>
              {currentStep > 1 ? '✓' : '1'}
            </div>
            <div className={`progress-line ${currentStep > 1 ? 'completed' : ''}`}></div>
          </div>
          <div className="progress-step-wrapper">
            <div className={`progress-circle ${currentStep >= 2 ? 'completed' : ''} ${currentStep === 2 ? 'current' : ''}`}>
              {currentStep > 2 ? '✓' : '2'}
            </div>
            <div className={`progress-line ${currentStep > 2 ? 'completed' : ''}`}></div>
          </div>
          <div className="progress-step-wrapper">
            <div className={`progress-circle ${currentStep >= 3 ? 'completed' : ''} ${currentStep === 3 ? 'current' : ''}`}>
              3
            </div>
          </div>
        </div>

        {/* Step 1 - Krijo llogarinë */}
        {currentStep === 1 && (
          <div className="wizard-step">
            <h2 className="wizard-title">{t('regw.step1Title')}</h2>
            <p className="wizard-subtitle">{t('regw.step1Subtitle')}</p>

            <div className="wizard-form two-column">
              <div className="form-row">
                <div className="form-field">
                  <label>Username</label>
                  {/*
                    * autoCapitalize/autoCorrect off: phone keyboards and some
                    * desktop clients capitalise the first letter of a field, and
                    * a capitalised identifier here is one the owner cannot type
                    * back at the login screen.
                    */}
                  <input
                    type="text"
                    placeholder={t('regw.typeHere')}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="username"
                    value={step1.username}
                    onChange={(e) => setStep1({ ...step1, username: e.target.value })}
                  />
                  {errors.username && <span className="field-error">{errors.username}</span>}
                </div>

                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder={t('regw.typeHere')}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="email"
                    value={step1.email}
                    onChange={(e) => setStep1({ ...step1, email: e.target.value })}
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>{t('regw.passwordLabel')}</label>
                  <input
                    type="password"
                    placeholder={t('regw.typeHere')}
                    value={step1.fjalekalimi}
                    onChange={(e) => setStep1({ ...step1, fjalekalimi: e.target.value })}
                  />
                  {errors.fjalekalimi && <span className="field-error">{errors.fjalekalimi}</span>}
                </div>

                <div className="form-field">
                  <label>{t('regw.confirmPasswordLabel')}</label>
                  <input
                    type="password"
                    placeholder={t('regw.typeHere')}
                    value={step1.konfirmoFjalekalimin}
                    onChange={(e) => setStep1({ ...step1, konfirmoFjalekalimin: e.target.value })}
                  />
                  {errors.konfirmoFjalekalimin && <span className="field-error">{errors.konfirmoFjalekalimin}</span>}
                </div>
              </div>
            </div>

            <div className="wizard-actions single">
              <button className="btn-primary" onClick={handleNext}>
                {t('regw.saveAndContinue')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 - Kompania */}
        {currentStep === 2 && (
          <div className="wizard-step">
            <h2 className="wizard-title">{t('regw.step2Title')}</h2>
            <p className="wizard-subtitle">{t('regw.step2Subtitle')}</p>

            <div className="wizard-form">
              <div className="form-field full-width">
                <label>{t('regw.companyNameLabel')}</label>
                <input
                  type="text"
                  placeholder="Viva Fresh"
                  value={step2.emriKompanise}
                  onChange={(e) => setStep2({ ...step2, emriKompanise: e.target.value })}
                />
                {errors.emriKompanise && <span className="field-error">{errors.emriKompanise}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.addressLabel')}</label>
                <input
                  type="text"
                  placeholder="Viva Fresh"
                  value={step2.adresa}
                  onChange={(e) => setStep2({ ...step2, adresa: e.target.value })}
                />
                {errors.adresa && <span className="field-error">{errors.adresa}</span>}
              </div>

              <div className="form-field">
                <label>Zip Code</label>
                <input
                  type="text"
                  placeholder="10000"
                  value={step2.zipCode}
                  onChange={(e) => setStep2({ ...step2, zipCode: e.target.value })}
                />
                {errors.zipCode && <span className="field-error">{errors.zipCode}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.cityLabel')}</label>
                <select
                  value={step2.qyteti}
                  onChange={(e) => setStep2({ ...step2, qyteti: e.target.value })}
                >
                  <option value="">{t('regw.selectCity')}</option>
                  <option value="Prishtinë">Prishtinë</option>
                  <option value="Prizren">Prizren</option>
                  <option value="Pejë">Pejë</option>
                  <option value="Gjakovë">Gjakovë</option>
                  <option value="Gjilan">Gjilan</option>
                  <option value="Ferizaj">Ferizaj</option>
                  <option value="Mitrovicë">Mitrovicë</option>
                </select>
                {errors.qyteti && <span className="field-error">{errors.qyteti}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.countryLabel')}</label>
                <select
                  value={step2.shteti}
                  onChange={(e) => setStep2({ ...step2, shteti: e.target.value })}
                >
                  <option value="">{t('regw.selectCountry')}</option>
                  <option value="Kosova">{t('regw.countryKosovo')}</option>
                  <option value="Shqipëri">{t('regw.countryAlbania')}</option>
                  <option value="Maqedoni e Veriut">{t('regw.countryNorthMacedonia')}</option>
                  <option value="Mali i Zi">{t('regw.countryMontenegro')}</option>
                </select>
                {errors.shteti && <span className="field-error">{errors.shteti}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.companyTypeLabel')}</label>
                <select
                  value={step2.llojiKompanise}
                  onChange={(e) => setStep2({ ...step2, llojiKompanise: e.target.value })}
                >
                  <option value="">{t('regw.selectType')}</option>
                  <option value="SH.P.K">SH.P.K</option>
                  <option value="N.SH">N.SH</option>
                  <option value="Sh.A">Sh.A</option>
                  <option value="OJQ">OJQ</option>
                </select>
                {errors.llojiKompanise && <span className="field-error">{errors.llojiKompanise}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.companySectorLabel')}</label>
                <select
                  value={step2.sektoriKompanise}
                  onChange={(e) => setStep2({ ...step2, sektoriKompanise: e.target.value })}
                >
                  <option value="">{t('regw.selectSector')}</option>
                  <option value="Bujqësi">{t('regw.sectorAgriculture')}</option>
                  <option value="Ndërtimtari">{t('regw.sectorConstruction')}</option>
                  <option value="Teknologji">{t('regw.sectorTechnology')}</option>
                  <option value="Shërbime">{t('regw.sectorServices')}</option>
                  <option value="Prodhim">{t('regw.production')}</option>
                  <option value="Tregti">{t('regw.sectorTrade')}</option>
                </select>
                {errors.sektoriKompanise && <span className="field-error">{errors.sektoriKompanise}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.onsiteActivitiesLabel')}</label>
                <select
                  value={step2.aktivitetetOnSite}
                  onChange={(e) => setStep2({ ...step2, aktivitetetOnSite: e.target.value })}
                >
                  <option value="">{t('regw.onsiteActivitiesLabel')}</option>
                  <option value="Prodhim">{t('regw.production')}</option>
                  <option value="Përpunim">{t('regw.processing')}</option>
                  <option value="Montim">{t('regw.assembly')}</option>
                  <option value="Shitje me pakicë">{t('regw.retail')}</option>
                  <option value="Shitje me shumicë">{t('regw.wholesale')}</option>
                </select>
                {errors.aktivitetetOnSite && <span className="field-error">{errors.aktivitetetOnSite}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.offsiteActivitiesLabel')}</label>
                <select
                  value={step2.aktivitetetOffSite}
                  onChange={(e) => setStep2({ ...step2, aktivitetetOffSite: e.target.value })}
                >
                  <option value="">{t('regw.offsiteActivitiesLabel')}</option>
                  <option value="Transport">Transport</option>
                  <option value="Shpërndarje">{t('regw.distribution')}</option>
                  <option value="Logjistikë">{t('regw.logistics')}</option>
                  <option value="Mirëmbajtje">{t('regw.maintenance')}</option>
                </select>
              </div>

              <div className="form-field">
                <label>{t('regw.sustainabilityTargetLabel')}</label>
                <input
                  type="text"
                  placeholder={t('regw.targetPlaceholder')}
                  value={step2.targetiQendrueshmrise}
                  onChange={(e) => setStep2({ ...step2, targetiQendrueshmrise: e.target.value })}
                />
              </div>

              <div className="form-field">
                <label>{t('regw.usagePurposeLabel')}</label>
                <input
                  type="text"
                  placeholder={t('regw.typeHere')}
                  value={step2.qellimiPerdorimit}
                  onChange={(e) => setStep2({ ...step2, qellimiPerdorimit: e.target.value })}
                />
              </div>

              <div className="form-field">
                <label>{t('regw.reportingTypeLabel')}</label>
                <input
                  type="text"
                  placeholder={t('regw.typeHere')}
                  value={step2.perQfareLlojRaportimi}
                  onChange={(e) => setStep2({ ...step2, perQfareLlojRaportimi: e.target.value })}
                />
              </div>
            </div>

            <div className="wizard-actions">
              <button className="btn-secondary" onClick={handleBack}>
                {t('regw.goBack')}
              </button>
              <button className="btn-primary" onClick={handleNext}>
                {t('regw.saveAndContinue')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 - Ndërtesat */}
        {currentStep === 3 && (
          <div className="wizard-step">
            <h2 className="wizard-title">{t('regw.step3Title')}</h2>
            <p className="wizard-subtitle">{t('regw.step3Subtitle')}</p>

            <div className="wizard-form">
              <div className="form-field">
                <label>{t('regw.nameLabel')}</label>
                <input
                  type="text"
                  placeholder="Velani"
                  value={step3.emri}
                  onChange={(e) => setStep3({ ...step3, emri: e.target.value })}
                />
                {errors.emri && <span className="field-error">{errors.emri}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.buildingLabel')}</label>
                <select
                  value={step3.ndertesa}
                  onChange={(e) => setStep3({ ...step3, ndertesa: e.target.value })}
                >
                  <option value="">{t('regw.selectType')}</option>
                  <option value="Objekt industrial">{t('regw.buildingIndustrial')}</option>
                  <option value="Objekt tregtar">{t('regw.buildingCommercial')}</option>
                  <option value="Objekt banimi">{t('regw.buildingResidential')}</option>
                  <option value="Objekt administrativ">{t('regw.buildingAdministrative')}</option>
                  <option value="Objekt të përzier">{t('regw.buildingMixed')}</option>
                </select>
                {errors.ndertesa && <span className="field-error">{errors.ndertesa}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.addressLabel')}</label>
                <input
                  type="text"
                  placeholder="123 Main Street, Prishtina 10000, Kosovo"
                  value={step3.adresa}
                  onChange={(e) => setStep3({ ...step3, adresa: e.target.value })}
                />
                {errors.adresa && <span className="field-error">{errors.adresa}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.cityLabel')}</label>
                <input
                  type="text"
                  placeholder="Prishtinë"
                  value={step3.qyteti}
                  onChange={(e) => setStep3({ ...step3, qyteti: e.target.value })}
                />
                {errors.qyteti && <span className="field-error">{errors.qyteti}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.countryLabel')}</label>
                <select
                  value={step3.shteti}
                  onChange={(e) => setStep3({ ...step3, shteti: e.target.value })}
                >
                  <option value="">{t('regw.selectCountry')}</option>
                  <option value="Kosova">{t('regw.countryKosovo')}</option>
                  <option value="Shqipëri">{t('regw.countryAlbania')}</option>
                  <option value="Maqedoni e Veriut">{t('regw.countryNorthMacedonia')}</option>
                  <option value="Mali i Zi">{t('regw.countryMontenegro')}</option>
                </select>
                {errors.shteti && <span className="field-error">{errors.shteti}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.constructionYearLabel')}</label>
                <input
                  type="number"
                  placeholder="2005"
                  value={step3.vitiNdertimit}
                  onChange={(e) => setStep3({ ...step3, vitiNdertimit: e.target.value })}
                />
                {errors.vitiNdertimit && <span className="field-error">{errors.vitiNdertimit}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.buildingSizeLabel')}</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="450"
                    value={step3.madhesia}
                    onChange={(e) => setStep3({ ...step3, madhesia: e.target.value })}
                  />
                  <span className="unit-label">m²</span>
                </div>
                {errors.madhesia && <span className="field-error">{errors.madhesia}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.floorsLabel')}</label>
                <input
                  type="number"
                  placeholder="3"
                  value={step3.numriKateve}
                  onChange={(e) => setStep3({ ...step3, numriKateve: e.target.value })}
                />
                {errors.numriKateve && <span className="field-error">{errors.numriKateve}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.primaryEnergyLabel')}</label>
                <select
                  value={step3.burimiKryesorEnergji}
                  onChange={(e) => setStep3({ ...step3, burimiKryesorEnergji: e.target.value })}
                >
                  <option value="">{t('regw.selectPrimarySource')}</option>
                  <option value="Energji elektrike nga rrjeti">{t('regw.energyGridElectricity')}</option>
                  <option value="Gaz natyror">{t('regw.energyNaturalGas')}</option>
                  <option value="Ngrohje qendrore (rrjet i qytetit)">{t('regw.energyDistrictHeating')}</option>
                  <option value="Gaz i lëngshëm (LPG – propan / butan)">{t('regw.energyLpg')}</option>
                  <option value="Naftë për ngrohje / dizel">{t('regw.energyHeatingOil')}</option>
                  <option value="Biomasë (dru, peleta, etj.)">{t('regw.energyBiomass')}</option>
                  <option value="Energji e rinovueshme në objekt (diellore, erë, gjeotermale)">{t('regw.energyOnsiteRenewable')}</option>
                  <option value="Burime të përziera energjie">{t('regw.energyMixed')}</option>
                  <option value="E panjohur">{t('regw.energyUnknown')}</option>
                </select>
                {errors.burimiKryesorEnergji && <span className="field-error">{errors.burimiKryesorEnergji}</span>}
              </div>

              <div className="form-field">
                <label>{t('regw.secondaryEnergyLabel')}</label>
                <select
                  value={step3.burimiDytesorEnergji}
                  onChange={(e) => setStep3({ ...step3, burimiDytesorEnergji: e.target.value })}
                >
                  <option value="">{t('regw.none')}</option>
                  <option value="Energji elektrike nga rrjeti">{t('regw.energyGridElectricity')}</option>
                  <option value="Gaz natyror">{t('regw.energyNaturalGas')}</option>
                  <option value="Ngrohje qendrore (rrjet i qytetit)">{t('regw.energyDistrictHeating')}</option>
                  <option value="Gaz i lëngshëm (LPG – propan / butan)">{t('regw.energyLpg')}</option>
                  <option value="Naftë për ngrohje / dizel">{t('regw.energyHeatingOil')}</option>
                  <option value="Biomasë (dru, peleta, etj.)">{t('regw.energyBiomass')}</option>
                  <option value="Energji e rinovueshme në objekt (diellore, erë, gjeotermale)">{t('regw.energyOnsiteRenewable')}</option>
                  <option value="Burime të përziera energjie">{t('regw.energyMixed')}</option>
                  <option value="E panjohur">{t('regw.energyUnknown')}</option>
                </select>
              </div>

              <div className="form-field">
                <label>{t('regw.waterSupplyLabel')}</label>
                <select
                  value={step3.furnizimetUje}
                  onChange={(e) => setStep3({ ...step3, furnizimetUje: e.target.value })}
                >
                  <option value="">{t('regw.selectType')}</option>
                  <option value="Rrjet publik">{t('regw.waterPublicNetwork')}</option>
                  <option value="Pus privat">{t('regw.waterPrivateWell')}</option>
                  <option value="Burim natyror">{t('regw.waterNaturalSource')}</option>
                </select>
                {errors.furnizimetUje && <span className="field-error">{errors.furnizimetUje}</span>}
              </div>
            </div>

            {submitError && <div className="submit-error">{submitError}</div>}
            {submitSuccess && <div className="submit-success">{submitSuccess}</div>}

            <div className="wizard-actions">
              <button className="btn-secondary" onClick={handleBack}>
                {t('regw.goBack')}
              </button>
              <button className="btn-primary" onClick={handleSubmit}>
                {t('regw.finish')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationWizard;

